/**
 * AuthEventBus — typed, synchronous domain event emitter.
 *
 * Decouples auth domain outcomes (sign-up, social sign-in, session lifecycle)
 * from their observers (GTM analytics, observability, modal orchestration).
 *
 * Design principles:
 * - Synchronous: subscribers are called inline during `emit`, in subscription order.
 * - Fire-and-forget: subscribers must not throw. Exceptions are swallowed and logged.
 * - Zero external dependencies: plain ES class, safe for SSR.
 * - Typed: each event type is narrowed to its exact payload via conditional types.
 */

import type { AuthEvent } from '../types/events';

type AuthEventHandler<T extends AuthEvent> = (event: T) => void;

export class AuthEventBus {

  //#region Fields

  private readonly handlers = new Map<string, Set<AuthEventHandler<AuthEvent>>>();
  private debug = false;

  //#endregion

  //#region Public API

  /**
   * Enables console logging for every emitted event.
   * Called once from `AuthSession.init()` when `debug: true` is passed to `initAuth()`.
   *
   * In the browser console you will see:
   *   [auth:event] sign_up_complete { user: { id: 42, ... }, newsletterConsent: false }
   */
  enableDebug(): void {
    this.debug = true;
  }

  /**
   * Registers a handler for the given event type.
   * Returns an unsubscribe function — call it to remove the handler.
   *
   * @example
   * const off = bus.on('sign_up_complete', event => console.log(event.user.id));
   * // later:
   * off();
   */
  on<K extends AuthEvent['type']>(
    type: K,
    handler: AuthEventHandler<Extract<AuthEvent, { type: K }>>,
  ): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as AuthEventHandler<AuthEvent>);
    return () => { set!.delete(handler as AuthEventHandler<AuthEvent>); };
  }

  /**
   * Emits an event to all registered handlers for its type.
   * Errors thrown by subscribers are swallowed — one bad subscriber
   * must not prevent others from receiving the event.
   */
  emit(event: AuthEvent): void {
    if (this.debug) {
      console.debug(
        `%c[auth:event]%c ${event.type}`,
        'color: #7c3aed; font-weight: bold',
        'color: inherit',
        event,
      );
    }

    const set = this.handlers.get(event.type);
    if (!set || set.size === 0) return;

    for (const handler of set) {
      try {
        handler(event);
      } catch (err) {
        console.error(`[auth:event] subscriber error for "${event.type}":`, err);
      }
    }
  }

  //#endregion
}
